import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useIMask } from 'react-imask'

import { ITextInputProps } from './ITextInputProps'
import { TextInput } from './TextInput'

interface Props extends ITextInputProps {
  mask?:
    | 'float'
    | 'integer'
    | 'money'
    | 'zipCode'
    | 'creditCard'
    | 'numberString'
    | 'monthYear'
    | 'phone'
    | 'cvv'
    | 'cpf'
    | 'date'
  onChange: (value: any) => void
}

export interface IMaskedInput {
  setValue: (value: any) => void
}

const Component: React.ForwardRefRenderFunction<IMaskedInput, Props> = (
  { value, onChange, mask, onBlur, ...props },
  forwardedRef,
) => {
  const opts = useMemo<any>(() => {
    switch (mask) {
      case 'money':
        return {
          mask: 'R$ num',
          blocks: {
            num: {
              mask: Number,
              scale: 2,
              padFractionalZeros: true,
              normalizeZeros: true,
              radix: ',',
              mapToRadix: ['.'],
              thousandsSeparator: '.',
            }
          }
        }
      case 'float':
        return {
          mask: Number,
          scale: 2,
          padFractionalZeros: false,
          normalizeZeros: false,
          radix: ',',
          mapToRadix: ['.'],
        }
      case 'integer':
      case 'numberString':
        return {
          mask: Number,
          scale: 0,
          signed: false,
          thousandsSeparator: '',
        }
      case 'cpf':
        return { mask: '000.000.000-00' }
      case 'zipCode':
        return { mask: '00000-000' }
      case 'phone':
        return {
          mask: [
            { mask: '(00) 0000-0000' },
            { mask: '(00) 00000-0000' }
          ]
        }
      case 'creditCard':
        return { mask: '0000 0000 0000 0000' }
      case 'date':
        return { mask: '00/00/0000' }
      case 'monthYear':
        return { mask: '00/00' }
      case 'cvv':
        return { mask: '0000' }
      default:
        return { mask: /.*/ }
    }
  }, [mask])

  const [lastEmitted, setLastEmitted] = useState<any>(undefined)

  const handleAccept = useCallback(
    (v: string, maskRef: any) => {
      let emitValue: any = v

      if (mask === 'float' || mask === 'money') {
        const unmasked = maskRef.unmaskedValue
        if (unmasked) {
          const num = parseFloat(unmasked)
          emitValue = isNaN(num) ? 0 : num
        } else {
          emitValue = 0
        }
      } else if (mask === 'integer') {
        const unmasked = maskRef.unmaskedValue
        if (unmasked) {
          const num = parseInt(unmasked, 10)
          emitValue = isNaN(num) ? 0 : num
        } else {
          emitValue = 0
        }
      } else if (mask === 'numberString') {
        emitValue = maskRef.unmaskedValue
      } else {
        emitValue = v
      }

      setLastEmitted(emitValue)
      onChange && onChange(emitValue)
    },
    [mask, onChange]
  )

  const { ref: inputRef, setUnmaskedValue, setValue, value: maskedValue } = useIMask(opts, {
    onAccept: handleAccept,
  })

  // Sync external value changes into IMask if they differ from what we just emitted
  useEffect(() => {
    if (value === undefined || value === null) {
      setValue('')
      return
    }

    if (value !== lastEmitted) {
      if (mask === 'money' || mask === 'float') {
        setUnmaskedValue(String(value))
      } else {
        setValue(String(value))
      }
    }
  }, [value, mask, setValue, setUnmaskedValue, lastEmitted])

  useImperativeHandle(forwardedRef, () => ({
    setValue(v: any) {
      if (mask === 'money' || mask === 'float') {
        setUnmaskedValue(String(v ?? ''))
      } else {
        setValue(String(v ?? ''))
      }
    },
  }))

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(e)
    },
    [onBlur]
  )

  return (
    <TextInput
      {...props}
      ref={inputRef as React.RefObject<HTMLInputElement>}
      onBlur={handleBlur}
      value={maskedValue}
      onChange={undefined}
    />
  )
}

export const MaskedInput = forwardRef(Component)
